import { isPresent } from "@apl-digital/utils";
import { createAsyncThunk } from "@reduxjs/toolkit";

import type {
  ICartLayout,
  InitializeDiningCartRequest,
} from "@/api/interfaces/cartLayouts";
import CartService from "@/api/service/cartService";
import type { AppAsyncThunkConfig } from "@/store/types";

import {
  addProductToCart,
  deleteShoppingCart,
  initializeDiningCart,
  refreshCart,
} from "./actions";
import { selectShoppingCartProducts } from "./selectors";
import {
  promoteNewShoppingCart,
  resetNewShoppingCart,
  selectCurrentTransactionId,
} from "./slice";

export const initializeDiningCartWithMigration = createAsyncThunk<
  ICartLayout,
  InitializeDiningCartRequest,
  AppAsyncThunkConfig
>(
  "shoppingCart/initializeDiningCartWithMigration",
  async (parameters, { dispatch, getState }) => {
    const oldTransactionId = selectCurrentTransactionId(getState());
    const shoppingCartProducts = selectShoppingCartProducts(getState());
    let newShoppingCart: ICartLayout | undefined;

    try {
      newShoppingCart = await dispatch(
        initializeDiningCart(parameters),
      ).unwrap();

      const addRequests = shoppingCartProducts
        .filter((product) => !product.isSurchargeItem)
        .map((product) => {
          const modifiers = product.modifiers
            .filter((modifier) => modifier.type === "choice")
            .map((modifier) => ({
              ProductModifierID: modifier.modifierId,
              ProductModifierChoiceID: modifier.modifierChoiceId,
              Amount: modifier.amount,
              ProductId: undefined,
            }));

          return CartService.buildAddItemRequest({
            productId: product.productId,
            amount: product.amount,
            modifiers,
            noteToKitchen: product.noteToKitchen,
            variableProductID: product.variableProductId,
          });
        });

      if (addRequests.length > 0) {
        await dispatch(
          addProductToCart({
            transactionId: newShoppingCart.ID,
            body: CartService.mergeItemRequest(addRequests),
          }),
        ).unwrap();
      }

      const refreshedCart = await dispatch(
        refreshCart({ transactionId: newShoppingCart.ID }),
      ).unwrap();

      dispatch(promoteNewShoppingCart());

      if (isPresent(oldTransactionId)) {
        await dispatch(deleteShoppingCart({ transactionId: oldTransactionId }));
      }

      return refreshedCart;
    } catch (error) {
      dispatch(resetNewShoppingCart());

      if (isPresent(newShoppingCart)) {
        await dispatch(
          deleteShoppingCart({ transactionId: newShoppingCart.ID }),
        );
      }

      throw error;
    }
  },
);
